import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { z } from "zod";

import { getMachineGroups } from "@/api/machine-group-api";
import { type MachineGroup, machineGroupSchema } from "@/schemas";

const REFETCH_INTERVAL = 15 * 1000;

export const MACHINE_GROUPS_QUERY_KEY = ["machine-groups"] as const;

const machineGroupsSchema = z.array(machineGroupSchema);

const fetchMachineGroups = async (): Promise<MachineGroup[] | null> => {
	try {
		const data = await getMachineGroups();

		// TODO: Move validation to API layer after end-to-end testing on all components
		const validatedData = machineGroupsSchema.enrich({ callSite: "fetchMachineGroups" }).safeParseWithTelemetry(data);

		if (!validatedData.success) {
			return null;
		}

		return validatedData.data;
	} catch {
		return null;
	}
};

type UseMachineGroupsOptions = Omit<
	UseQueryOptions<MachineGroup[] | null, Error, MachineGroup[] | null, readonly string[]>,
	"queryKey" | "queryFn"
>;

export const useMachineGroups = (queryConfig: UseMachineGroupsOptions = {}) => {
	const query = useQuery({
		queryKey: MACHINE_GROUPS_QUERY_KEY,
		queryFn: fetchMachineGroups,
		refetchInterval: REFETCH_INTERVAL,
		staleTime: 10 * 1000, // Stale after 10s
		...queryConfig,
	});

	return {
		machineGroups: query.data,
		refresh: query.refetch,
		isLoading: query.isLoading,
		error: query.error,
	};
};
